/**
 * How a project's URLs are spelled.
 *
 * Deliberately separate from sections.js, and deliberately importing nothing:
 * sections.js builds JSX elements at module scope, and the things that need to
 * *build a URL* (Linked/recordKinds.js, the editor's internal links, the
 * Dashboard) are reachable from those same elements. Putting these helpers in
 * sections.js made an import cycle - sections → Bugs → BugTaskPanel →
 * LinkedRecord → recordKinds → sections - in which a component could evaluate
 * as undefined depending on which module webpack reached first. This file is a
 * leaf, so it can't.
 */

/** Where a project opens when its URL names no section. */
export const DEFAULT_SECTION = "home";

/**
 * The tabs a section is divided into, as URL segments.
 *
 * Only sections whose tabs are a fixed, named set appear here. A tab used to be
 * an index into an array held in component state, which meant it couldn't be
 * linked to, survive a reload, or be reached with the back button - and on the
 * Bugs page it had grown a second problem, inferring which tab was open from
 * whether a bug id happened to be in the URL. Settings had a third: it stored a
 * bare index and clamped it against a permission-filtered list, so the same
 * number meant different tabs for different people.
 *
 * Labels live here rather than in sections.js because this module is a leaf -
 * see the note at the top. The components each tab renders are attached in
 * sections.js, which is the half that can import them.
 *
 * `permission` hides a tab from someone who doesn't hold it, same "just don't
 * offer it" approach as the sidebar.
 *
 * `containerClass`/`contentClass` are the wrappers each section's own stylesheet
 * hangs its layout off (heights, flex, overflow) - they were markup in the three
 * components this replaced, so they have to be carried rather than dropped.
 *
 * Scope is deliberately absent: its tabs are one per scope class, created and
 * deleted by the user with live item counts in the labels, so they can't be a
 * fixed list. It declares its own routes - see projectRoutes.js.
 */
export const SECTION_TABS = {
    bugs: {
        defaultTab: "review",
        containerClass: "bugs-container",
        contentClass: "tabs-content bugs-tabs-content",
        tabs: [
            {slug: "add", label: "Add Bug"},
            {slug: "review", label: "Review Bugs"},
        ],
    },
    support: {
        defaultTab: "queue",
        containerClass: "support-container",
        contentClass: "tabs-content support-tabs-content",
        tabs: [
            {slug: "new", label: "New Ticket"},
            {slug: "queue", label: "Support Queue"},
            {slug: "faq", label: "FAQ"},
        ],
    },
    settings: {
        defaultTab: "swimlanes",
        containerClass: "project-settings-container",
        contentClass: "tabs-content",
        // Every one of these writes through a MANAGE_* permission (see the
        // gating in api/routes/*.js). Users (member/role management) and the
        // tabs it writes to all fall under MANAGE_PROJECT_SETTINGS; Calendar
        // has its own.
        tabs: [
            {slug: "swimlanes", label: "Swimlanes", permission: "MANAGE_PROJECT_SETTINGS"},
            {slug: "tasks", label: "Tasks", permission: "MANAGE_PROJECT_SETTINGS"},
            {slug: "bugs", label: "Bugs", permission: "MANAGE_PROJECT_SETTINGS"},
            {slug: "support", label: "Support", permission: "MANAGE_PROJECT_SETTINGS"},
            {slug: "users", label: "Users", permission: "MANAGE_PROJECT_SETTINGS"},
            {slug: "images", label: "Images", permission: "MANAGE_PROJECT_SETTINGS"},
            {slug: "calendar", label: "Calendar", permission: "MANAGE_CALENDAR"},
            {slug: "api", label: "API Access", permission: "MANAGE_PROJECT_SETTINGS"},
        ],
    },
};

export const tabsFor = (sectionSlug) => SECTION_TABS[sectionSlug] || null;

export const defaultTabFor = (sectionSlug) => SECTION_TABS[sectionSlug]?.defaultTab ?? null;

/**
 * The URL of a section within a project.
 *
 * Kanban is the one section that carries a sprint in its path - its board only
 * ever shows one sprint, and which one belongs in the address. That rule used
 * to live inside pages/Project.js, so everywhere else that wanted a Kanban URL
 * re-derived it by hand and disagreed. Pass `sprintIndex` when the caller knows
 * which sprint is meant; leave it off - or pass the -1 sentinel - to let the
 * board fall back to whichever sprint the store last remembered.
 *
 * `projectId` is the project's publicId - what the URL carries and what the
 * store holds as `project_id`, not the database id.
 */
export const sectionPath = (projectId, slug, {sprintIndex, tab} = {}) => {
    // A negative index is the "no sprint selected" sentinel the store and the
    // sprint dropdown both use (see pages/Project.js's effectiveSprintIndex).
    // It names no sprint, so it doesn't belong in a URL - /sprint/-1/kanban
    // resolves to the same board as a plain /kanban, by the same fallback, but
    // says something false on the way there.
    const namesASprint = slug === "kanban" && sprintIndex !== undefined
        && sprintIndex !== null && Number(sprintIndex) >= 0;
    if (namesASprint) {
        return `/project/${projectId}/sprint/${sprintIndex}/kanban`;
    }

    // A tabbed section's canonical URL names its tab. Left off, the section's
    // default tab is used, so callers that don't care about tabs still get an
    // address that doesn't immediately redirect; pass `tab: null` to get the
    // bare section URL and let the index route sort it out.
    const resolved = tab === undefined ? defaultTabFor(slug) : tab;
    return resolved
        ? `/project/${projectId}/${slug}/${resolved}`
        : `/project/${projectId}/${slug}`;
};

/**
 * Which section a URL is showing.
 *
 * The section is a literal path segment rather than a `:route` param (see
 * projectRoutes.js), so it can't be read with useParams - and useMatches needs
 * a data router, which this app doesn't use. Reading it off the path is the
 * remaining way, and only the layout needs it, to highlight the sidebar and
 * pick the content wrapper.
 *
 * `/project/:id/sprint/:n/kanban` is the one shape where the third segment
 * isn't the section - Kanban's sprint sits in front of it.
 */
export const sectionSlugFromPath = (pathname) => {
    const [, , , third] = pathname.split("/");
    return third === "sprint" ? "kanban" : (third || DEFAULT_SECTION);
};

/**
 * Which tab of a tabbed section a URL is showing, or null.
 *
 * The segment after the section, checked against that section's own tabs so an
 * overlay segment ("bug", "task") on an untabbed URL isn't mistaken for one.
 */
export const tabSlugFromPath = (pathname) => {
    const sectionSlug = sectionSlugFromPath(pathname);
    const config = tabsFor(sectionSlug);
    if (!config) return null;

    const [, , , , fourth] = pathname.split("/");
    return config.tabs.some((tab) => tab.slug === fourth) ? fourth : null;
};

/**
 * Views that render an opened record themselves, so the project-level overlay
 * stands aside rather than putting a second copy of it on screen.
 *
 * Keyed by view - a section, or a section and tab - because ownership is that
 * precise: the bug list shows a bug in its own slide-in panel, but the Add Bug
 * tab beside it has no list to select within, so a bug opened there wants the
 * overlay like anywhere else.
 *
 * This replaced a hardcoded `route !== "bugs"` test in pages/Project.js. The
 * support queue had the same need and no such test, so a ticket could be open
 * in the queue's panel and in the overlay at once - which stops being possible
 * when the rule is a table every kind is looked up in.
 */
export const RECORDS_RENDERED_BY_VIEW = {
    "bugs/review": ["bug"],
    "support/queue": ["support"],
};

/**
 * The view a URL is showing, as "section" or "section/tab" - the key
 * RECORDS_RENDERED_BY_VIEW is written in.
 */
export const viewKeyFromPath = (pathname) => {
    const section = sectionSlugFromPath(pathname);
    const tab = tabSlugFromPath(pathname);
    return tab ? `${section}/${tab}` : section;
};

export const viewRendersRecord = (pathname, kind) => (
    (RECORDS_RENDERED_BY_VIEW[viewKeyFromPath(pathname)] || []).includes(kind)
);
